import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import PropertyCard from '../listings/PropertyCard';
import publicListingsService from '../../services/publicListingsService';
import { useFeatures } from '../../contexts/FeaturesContext';
import { text } from '../../theme/colors';

const MAX = 6;

/**
 * "Saved homes" on the client dashboard: the newest handful of listings the
 * buyer hearted, straight from `GET /api/listings/saved`.
 *
 * It is a shortcut, not a workspace — no heart, no filters — so a buyer who
 * wants to manage the list follows "See all saved" to `/properties?saved=1`,
 * which is the same data with the full browse UI around it. The section hides
 * itself when the feature is off, while it loads, on an error, and when nothing
 * is saved: an empty "Saved homes" heading would just be clutter on a dashboard
 * whose owner may never have opened the property pages at all.
 */
export default function SavedHomesCard() {
  const { listings: listingsOn } = useFeatures();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!listingsOn) return undefined;
    let live = true;
    publicListingsService.saved()
      .then((res) => { if (live) setItems(res?.data?.items || []); })
      .catch(() => { /* a dashboard extra is never worth an error banner */ });
    return () => { live = false; };
  }, [listingsOn]);

  if (!listingsOn || items.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: text.primary }}>Saved homes</Typography>
        <Button
          component={RouterLink}
          to="/properties?saved=1"
          size="small"
          endIcon={<ArrowForward />}
          sx={{ fontWeight: 600, textTransform: 'none' }}
        >
          See all saved
        </Button>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
        {items.slice(0, MAX).map((l) => <PropertyCard key={l.id} listing={l} />)}
      </Box>
    </Box>
  );
}
